export const NEW_SESSION_PATH = "/workspace/sessions/new";

type NewSessionRouteOptions = {
  channelId?: string | null;
  dataSetId?: string | null;
  inboxItemId?: string | null;
  pathname?: string | null;
  returnTo?: string | null;
  search?: string | null;
  sourceRunId?: string | null;
  sourceSessionId?: string | null;
  viewId?: string | null;
  workspaceView?: string | null;
};

function trimmed(value: string | null | undefined): string | null {
  const next = value?.trim();
  return next ? next : null;
}

export function channelIdFromWorkspacePath(
  pathname: string | null | undefined,
): string | null {
  const match = pathname?.match(/^\/workspace\/channels\/([^/]+)\/?$/);
  const channelId = match?.[1]?.trim();
  if (!channelId || channelId === "new") return null;
  try {
    return decodeURIComponent(channelId);
  } catch {
    return channelId;
  }
}

export function safeWorkspaceReturnTo(
  value: string | null | undefined,
): string | null {
  const candidate = trimmed(value);
  if (
    !candidate ||
    candidate.startsWith("//") ||
    candidate.includes("\\") ||
    (candidate !== "/workspace" && !candidate.startsWith("/workspace/"))
  ) {
    return null;
  }

  try {
    const parsed = new URL(candidate, "https://mlx.local");
    if (parsed.origin !== "https://mlx.local") return null;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export function workspaceReturnTo(
  pathname: string | null | undefined,
  search?: string | null,
): string | null {
  const normalizedSearch = trimmed(search)?.replace(/^\?/, "");
  return safeWorkspaceReturnTo(
    `${pathname ?? ""}${normalizedSearch ? `?${normalizedSearch}` : ""}`,
  );
}

export function viewIdFromWorkspaceSearch(
  search: string | null | undefined,
): string | null {
  const selection = new URLSearchParams(
    trimmed(search)?.replace(/^\?/, "") ?? "",
  )
    .get("view")
    ?.trim();
  if (!selection?.startsWith("saved:")) return null;
  return trimmed(selection.slice("saved:".length));
}

export function dataSetIdFromWorkspaceSearch(
  search: string | null | undefined,
): string | null {
  return trimmed(
    new URLSearchParams(trimmed(search)?.replace(/^\?/, "") ?? "").get(
      "dataSet",
    ),
  );
}

export function workspaceViewFromWorkspaceSearch(
  search: string | null | undefined,
): string | null {
  return trimmed(
    new URLSearchParams(trimmed(search)?.replace(/^\?/, "") ?? "").get("view"),
  );
}

export function buildNewSessionHref(
  options: NewSessionRouteOptions = {},
): string {
  const query = new URLSearchParams();
  const channelId =
    trimmed(options.channelId) ?? channelIdFromWorkspacePath(options.pathname);
  const dataSetId =
    trimmed(options.dataSetId) ?? dataSetIdFromWorkspaceSearch(options.search);
  const inboxItemId = trimmed(options.inboxItemId);
  const sourceSessionId = trimmed(options.sourceSessionId);
  const sourceRunId = trimmed(options.sourceRunId);
  const viewId =
    trimmed(options.viewId) ?? viewIdFromWorkspaceSearch(options.search);
  const workspaceView =
    trimmed(options.workspaceView) ??
    workspaceViewFromWorkspaceSearch(options.search);
  const returnTo =
    safeWorkspaceReturnTo(options.returnTo) ??
    workspaceReturnTo(options.pathname, options.search);

  if (channelId) query.set("channelId", channelId);
  if (channelId && dataSetId) query.set("dataSetId", dataSetId);
  if (channelId && viewId) query.set("viewId", viewId);
  if (channelId && workspaceView) query.set("workspaceView", workspaceView);
  if (inboxItemId) query.set("inboxItemId", inboxItemId);
  if (sourceSessionId && sourceRunId) {
    query.set("sourceSessionId", sourceSessionId);
    query.set("sourceRunId", sourceRunId);
  }
  const returnPathname = returnTo
    ? new URL(returnTo, "https://mlx.local").pathname
    : null;
  if (returnTo && returnPathname !== NEW_SESSION_PATH)
    query.set("returnTo", returnTo);

  const encoded = query.toString();
  return encoded ? `${NEW_SESSION_PATH}?${encoded}` : NEW_SESSION_PATH;
}
